import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Provider } from 'react-redux';
import { ADD_TO_CART_EVENTAPI } from '../app/containers/Init/constants';
import { postData } from '../common/api';
import { createEmbeddedPageRequest } from '../common/apiRequest';
import {
  cvbFreeGiftAdd,
  fetchCart,
  getMRCartFromShopifyCart,
  updateCart,
} from '../common/cart';
import { freeGiftlocalStorageTTL, getOfferEndpoint } from '../common/constants';
import { createEmebeddedContainerWrapper } from '../common/html';
import {
  getCountryCode,
  getShop,
  getUTMData,
  isCartPage,
  isShopifyThemeEditor,
} from '../common/pageData';
import {
  getCVBAutoAddedVariantsLimits,
  getCVBFreeGiftConfig,
  getCVBManualFgOfferConfig,
  getCVBOfferID,
  getIsAutomaticDiscountEnabled,
  getIsShopifyFunctionsEnabled,
  getOrderID,
  getStorefrontAccessToken,
  setCVBAutoAddedVariantsLimits,
  setCVBFreeGiftConfig,
  setCVBManualFgOfferConfig,
  setCVBOfferID,
  setOrderID,
} from '../common/settings';
import { GlobalStyle } from '../app/components/skins';
// eslint-disable-next-line import/no-cycle
import Init from '../app/containers/Init';
import CartValueBarPage from '../app/containers/CartValueBarPage';
import { logger } from '../common/logger';
import { sleep } from '../common/time';
import { setOfferViewed } from '../common/offer';
import { getMerchantSpecificThemeFlags } from '../app/components/helpers/offer';
import { StaticMutexKeys, mutexManager } from '../common/mutexmanager';
import { storeShopifyFunctionsCVBData } from './shopifyFunctionsDiscount';
import {
  calculateCartAmount,
  calculateCartQuantity,
  getAllFreeGifts,
  getFgDataBasedOnMilestone,
  getFreeGifts,
} from '../app/containers/CartValueBarPage/cvb-calculation-functions';
import {
  executeFreegiftConditions,
  onManualFreeGiftEligibleResponse,
} from '../freegift';
import { goToCart } from '../common/navigation';
import { logRuntimeError, logtailMiddleware } from '../common/utils';

export const checkCVBAndDisplay = async ({
  retryContainerCreation,
  cartPageDiscountConfig, // optional
  isDiscountCodeEnabled,
  apiCart,
  widgets,
}) => {
  let widget = null;
  let widgetMutex = null;
  try {
    for (let i = 0; i < widgets.length; i += 1) {
      if (
        widgets[i].offerType === 'cvb' &&
        (widgets[i].triggerType === 'cart_page' ||
          widgets[i].triggerType === 'checkout_page')
      ) {
        widget = widgets[i];
        break;
      }
    }

    if (!widget) {
      logger.serverInfoLog('cvb widget not found', {
        functionName: 'checkCVBAndDisplay',
        fileName: 'cvbModule',
        extraInfoData: {
          widgets,
        },
      });
      return;
    }

    if (getShop() === 'muse-india.myshopify.com') {
      const utmData = getUTMData();
      if (utmData) {
        const { utm_source } = utmData;
        if (utm_source === 'snowball') {
          logger.log('skipping CVB');
          return;
        }
      }
    }

    widgetMutex = mutexManager.getMutex(StaticMutexKeys.CVB);
    await widgetMutex.acquire();
    logger.log('check cvb');
    const { mrWidgetID, sections } = widget;

    if (getShop() === 'athletics-motion.myshopify.com') {
      await sleep(1700);
    }
    if (getShop() === 'kiwi-bagineers.myshopify.com') {
      await sleep(1700);
    }
    let displayAfterMs = 0;
    if (getShop() === 'vilvahstore.myshopify.com') {
      displayAfterMs = 1000;
    }
    if (getShop() === 'matural-in.myshopify.com') {
      displayAfterMs = 1500;
    }

    if (getShop() === 'onlineshop-chocoversum.myshopify.com') {
      displayAfterMs = 1000;
    }

    const isAutomaticDiscountEnabled = getIsAutomaticDiscountEnabled();

    const source = 'cart_page';
    const req = await createEmbeddedPageRequest({
      source,
      offerType: 'cvb',
      apiCart,
      widget,
    });
    const { cart: mrCart, cart_level_discount_applications } = req;

    const response = await postData(getOfferEndpoint(), req);

    if (!response) {
      return;
    }

    logtailMiddleware({
      widgetId: widget.id,
      basicOfferData: {
        milestones: response.offer.milestones,
        mrOfferId: response.offer.mr_offer_id,
      },
      offerName: 'cvb',
    });

    if (getIsShopifyFunctionsEnabled() && response.is_eligible) {
      const sfModule = await import('./shopifyFunctionsDiscount');
      sfModule.storeShopifyFunctionsCVBData({ offer: response.offer });
      const { offer: modifiedOffer } = await sfModule.modifyCVBOffer({
        offer: response.offer,
        mrCart,
        accessToken: getStorefrontAccessToken(),
        countryCode: getCountryCode(),
        mrOrderID: getOrderID(),
        shopURL: req.shop_url,
        cartToken: req.cart_token,
        cart_level_discount_applications,
      });
      response.offer = modifiedOffer;
    }

    const { order_id, is_eligible, offer } = response;
    setOrderID(order_id);
    let cvbData = {};
    if (is_eligible) {
      cvbData = await onCVBResponse({
        offer,
        isAutomaticDiscountEnabled,
        mrCart,
        req,
        widget,
        cartPageDiscountConfig,
      });
    } else {
      await onCVBNotEligible({
        mrCart: req.cart,
        reservedItemQuantity: offer.reservedItemQuantity,
        widget,
      });
    }

    setTimeout(() => {
      displayCVB({
        is_eligible,
        offer,
        mrCart,
        retryContainerCreation,
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
        isAutomaticDiscountEnabled,
        mrWidgetID,
        sections: sections.filter(
          s => s.sectionType.split('-')[0] !== 'freegift',
        ),
        widgetID: widget.id,
        ...cvbData,
      });
    }, displayAfterMs);
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'cvbModule',
        level: 'error',
        functionName: 'checkCVBAndDisplay',
        widgets,
      },
      errorStack: e,
    });
    logger.log('error', e);
  } finally {
    if (widgetMutex) {
      widgetMutex.release();
    }
  }
};

const displayCVB = async ({
  is_eligible,
  offer,
  mrCart,
  retryContainerCreation,
  cartPageDiscountConfig, // optional parameter
  isDiscountCodeEnabled,
  isAutomaticDiscountEnabled,
  mrWidgetID,
  sections,
  widgetID,
  cartAmount,
  cartQuantity,
}) => {
  try {
    offer.widgetID = widgetID;
    const containers = [];
    for (let i = 0; i < sections.length; i += 1) {
      const section = sections[i];
      let { selector, position } = section;
      const { sectionType } = section;

      // TODO: change value in db
      if (
        getShop() === 'elloproducts.myshopify.com' &&
        sectionType === 'cart_popup' &&
        selector === ''
      ) {
        selector = '.cart-popup-content .cart-form';
        position = 'before';
      }

      if (sectionType === 'cart_page' && !isCartPage()) {
        continue;
      }

      containers.push({
        selector,
        position,
        containerID: getCVBContainerID({ sectionType, selector, mrWidgetID }),
      });
    }

    // if offer is not eligible then don't render
    if (!is_eligible) {
      for (let i = 0; i < containers.length; i += 1) {
        const { containerID } = containers[i];
        renderCVBInDiv({ close: true }, containerID);
      }
      return;
    }

    const { mr_offer_id } = offer;
    setCVBOfferID(mr_offer_id);
    const tasks = [];
    for (let i = 0; i < containers.length; i += 1) {
      const { containerID, selector, position } = containers[i];
      tasks.push(
        createContainerAndRender({
          offer,
          mrCart,
          containerID,
          selector,
          position,
          retryContainerCreation,
          isAutomaticDiscountEnabled,
          cartAmount,
          cartQuantity,
        }),
      );
    }

    const result = await Promise.allSettled(tasks);
    const rendered = result.reduce((acc, curr) => acc || curr.value, false);
    if (rendered) {
      //   TODO: find a better way for events
      //   add event
      await cvbAddEvent({ mrCart });
      await setOfferViewed({
        mr_offer_id,
        checkDiscount: true,
        cartPageDiscountConfig,
        isDiscountCodeEnabled,
      });
    }
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'cvbModule',
        level: 'error',
        functionName: 'displayCVB',
        extraErrorData: {
          milestones: offer?.milestones,
        },
      },
      errorStack: e,
    });
  }
};

const createContainerAndRender = async ({
  offer,
  mrCart,
  containerID,
  selector,
  position,
  retryContainerCreation,
  isAutomaticDiscountEnabled,
  cartAmount,
  cartQuantity,
}) => {
  const existing = document.getElementById(containerID);
  if (existing) {
    renderCVBInDiv(
      {
        offer,
        mrCart,
        isAutomaticDiscountEnabled,
        cartAmount,
        cartQuantity,
      },
      containerID,
    );
    return true;
  }

  const selectors = selector.split(',');
  const positions = position.split(',');
  let container;
  for (let i = 0; i < selectors.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    container = await createEmebeddedContainerWrapper({
      id: containerID,
      selector: selectors[i].trim(),
      position: positions[i],
      retryContainerCreation,
    });

    if (container) {
      break;
    }
  }

  if (container) {
    renderCVBInDiv(
      {
        offer,
        mrCart,
        isAutomaticDiscountEnabled,
        cartAmount,
        cartQuantity,
      },
      containerID,
    );
    return true;
  }
  return false;
};

export const cvbAddEvent = async ({ mrCart }) => {
  const mr_offer_id = getCVBOfferID();

  if (mr_offer_id === '') {
    return;
  }
  let cartToUse = mrCart;
  try {
    if (!mrCart) {
      const cart = await fetchCart();
      cartToUse = getMRCartFromShopifyCart(cart);
    }

    const req = {
      mr_offer_id,
      cart: cartToUse,
    };

    const apiURL = ADD_TO_CART_EVENTAPI;
    const httpArgs = [apiURL, req];
    await axios.post(...httpArgs);
  } catch (e) {
    logger.log(e);
    logRuntimeError({
      errorMessage: `fetch cart error ${e.name} ${e.message}`,
      errorData: {
        fileName: 'cvbModule',
        functionName: 'cvbAddEvent',
        level: 'error',
        extraErrorData: {
          cart: cartToUse,
          mr_offer_id,
        },
      },
      errorStack: e,
    });
  }
};

export function renderCVBInDiv(props, id) {
  try {
    const container = document.getElementById(id);
    if (!container) {
      throw new Error(`container not found for Id : ${id}`);
    }

    if (getShop() === 'vndmm.myshopify.com') {
      container.style.marginTop = '10px';
    }

    if (
      [
        'youvit-id.myshopify.com',
        'becausetees.myshopify.com',
        'cardologyltd.myshopify.com',
        'blue-paw-co.myshopify.com',
      ].includes(getShop())
    ) {
      container.style.width = '100%';
    }

    if (getShop() === 'sandstorm-kenya.myshopify.com') {
      container.style.margin = '20px 20px 0 20px';
    }

    if (getShop() === 'hardpressed-print-studo.myshopify.com') {
      container.style.margin = '0px 0px 10px 0px';
    }

    if (
      [
        'knack-roularta.myshopify.com',
        'libelle-lekker-roularta.myshopify.com',
        'test-sub-rmg.myshopify.com',
      ].includes(getShop())
    ) {
      container.style.width = '100%';
      container.style.marginTop = '30px';
    }

    logger.log('render cvb');
    const start = performance.now();
    // const THEME = createTheme({
    //   typography: {
    //     fontFamily: props.theme && props.theme.font,
    //   },
    // });

    const { offer, mrCart, isAutomaticDiscountEnabled, close } = props;

    if (close && isShopifyThemeEditor()) {
      // for shopify theme editor leave the div as it is
      // TODO: handle case where the offer is displayed and then removed
      // so that dummy values can be displayed
      return;
    }

    if (!close) {
      // when close is true offer gets hidden
      // modify theme only in case offer is not being hidden
      offer.theme = getMerchantSpecificThemeFlags({
        theme: offer.theme,
        shopURL: getShop(),
        offerType: 'cvb',
      });
    }

    ReactDOM.render(
      // <MuiThemeProvider theme={THEME}>
      <Provider store={store}>
        <div>
          <GlobalStyle {...props.theme} />
          <Init />
          {!close && (
            <CartValueBarPage
              mrCart={mrCart}
              offer={offer}
              isAutomaticDiscountEnabled={isAutomaticDiscountEnabled}
              shopURL={getShop()}
              cartAmount={props.cartAmount}
              cartQuantity={props.cartQuantity}
            />
          )}
        </div>
      </Provider>,
      container,
    );
    logger.Debug(performance.now() - start, 'render time');
  } catch (e) {
    logRuntimeError({
      errorMessage: `${e.name} ${e.message}`,
      errorData: {
        fileName: 'cvbModule',
        functionName: 'renderCVBInDiv',
        level: 'error',
      },
      errorStack: e,
    });
  }
}

const onCVBResponse = async ({
  offer,
  isAutomaticDiscountEnabled,
  mrCart,
  req,
  widget,
  cartPageDiscountConfig,
}) => {
  const { milestones, theme, isCartQuantityRestricted } = offer;
  const freegifts = [];
  for (let i = 0; i < milestones.length; i += 1) {
    const { rewards } = milestones[i];
    const freeGiftRewards = rewards.filter(
      r =>
        r.rewardType === 'freegift' &&
        r.freeGiftSpVariantID !== 0 &&
        r.freeGiftAvailingType === 'auto_add',
    );

    if (freeGiftRewards.length > 0) {
      freegifts.push(...freeGiftRewards);
    }
  }

  const config = {
    isCartQuantityRestricted,
    isEnabled: true,
    theme,
    ttl: new Date().getTime() + freeGiftlocalStorageTTL,
    allFreeGifts: freegifts,
  };

  setCVBFreeGiftConfig(JSON.stringify(config));

  const cvbData = await onCVBOfferEligible({
    offer,
    isAutomaticDiscountEnabled,
    mrCart,
    req,
    widget,
    cartPageDiscountConfig,
  });

  return cvbData;
};

const onCVBNotEligible = async ({ mrCart, reservedItemQuantity, widget }) => {
  const cvbFgConfig = getCVBFreeGiftConfig();
  const {
    isEnabled,
    theme,
    allFreeGifts,
    isCartQuantityRestricted,
  } = cvbFgConfig;

  if (!isEnabled) {
    return;
  }

  if (!isCartQuantityRestricted) {
    return;
  }

  const { isRefreshPageAfterProductAddition, cartDiscountText } = theme;

  const config = {
    isRefresh: isRefreshPageAfterProductAddition,
    isAutoRemove: true,
    isAutoAdd: false,
    allFreeGifts,
    freeGifts: [],
    mrCart,
    cartDiscountText,
    primaryVariantId: 0,
    isConsiderReservedItemQuantity: false,
  };
  await cvbFreeGiftAdd(config);

  const existingConfig = getCVBManualFgOfferConfig();
  await executeFreegiftConditions({
    mrCart,
    availingType: 'manual_selection',
    widget,
    isEligible: false,
    reservedItemQuantity,
    canBeAutoRemoved: true,
    useExistingConfig: true,
    existingConfig,
  });
};

let store = null;
export const setStore = st => {
  store = st;
};

// function to be called whenever an emebeded offer is eligible
async function onCVBOfferEligible({
  offer,
  isAutomaticDiscountEnabled,
  mrCart,
  req,
  widget,
  cartPageDiscountConfig,
}) {
  const { id: widgetID, sections } = widget;
  // if functions is enabled then store data
  if (getIsShopifyFunctionsEnabled()) {
    const sfModule = await import('./shopifyFunctionsDiscount');
    sfModule.storeShopifyFunctionsOfferData({ offer });
  }

  const shopURL = getShop();

  const {
    theme: { isRefreshPageAfterProductAddition, cartDiscountText },
    freeGiftAutoAddLimit,
    cvbEligibleProducts,
    milestones,
    reservedItemQuantity,
    isCartQuantityRestricted,
    isCVBAmountUsable,
    cvbCartValue,
    isCVBQuantityUsable,
    cvbCartQuantityValue,
    isCVBAddRewardsOfAllMilestones,
  } = offer;

  const isPriceValueType = milestones[0].valueType === 'price';
  const isQuantityValueType = milestones[0].valueType === 'quantity';
  let isUseDiscountedPriceFromCart =
    shopURL === 'hardpressed-print-studo.myshopify.com' ||
    isAutomaticDiscountEnabled;

  if (
    [
      'put-a-case-on-me.myshopify.com',
      'moil-de.myshopify.com',
      'moil-il.myshopify.com',
      'moil-dk.myshopify.com',
      'moil-se.myshopify.com',
    ].includes(shopURL)
  ) {
    isUseDiscountedPriceFromCart = false;
  }

  const newCartAmount = calculateCartAmount(
    mrCart,
    cvbEligibleProducts,
    milestones,
    reservedItemQuantity,
    isCartQuantityRestricted,
    isCVBAmountUsable,
    cvbCartValue,
    isUseDiscountedPriceFromCart,
  );

  // calculate and set cart qty
  const newCartQuantity = calculateCartQuantity(
    mrCart,
    cvbEligibleProducts,
    milestones,
    reservedItemQuantity,
    isCartQuantityRestricted,
    isCVBQuantityUsable,
    cvbCartQuantityValue,
  );

  // based on free new cart amount calculate free gift
  const { eligibleFreeGifts, fgMilestoneMap } = getFreeGifts({
    cartValue: newCartAmount,
    cartQuantity: newCartQuantity,
    milestones,
    isCVBAddRewardsOfAllMilestones,
    isPriceValueType,
    isQuantityValueType,
  });
  const freeGifts =
    eligibleFreeGifts.map(obj => ({
      ...obj,
      isPrimaryAndOfferedSame: false,
    })) || [];

  const { allFreeGifts, allFgMilestoneMap } = getAllFreeGifts(milestones);

  const fgVariantsMap = getFgVariantsMap(milestones);

  const totalFreeGiftUsedQuantityAutoAdd = getTotalFreeGiftUsedQuantityAutoAdd(
    milestones,
  );
  const totalFreeGiftUsedQuantity = getTotalFreeGiftUsedQuantityAutoAdd(
    milestones,
    true,
  );
  const getExtraAddedFgs = () => {
    const extraAddedFgs = {};
    const usedFgs = {};
    milestones.forEach((milestone, idx) => {
      const {
        freegiftItemQuantity,
        reservedItemQuantity: reservedItemQuantityMilestone,
      } = milestone;

      mrCart.forEach((item, idx) => {
        const updatedItem = { ...item };
        if (fgVariantsMap[item.sp_variant_id]) {
          const usedFg = usedFgs[idx] || 0;
          const milestoneReservedItemQuantity =
            reservedItemQuantityMilestone[idx] || 0;

          if (
            freegiftItemQuantity[idx] &&
            item.quantity > freegiftItemQuantity[idx]
          ) {
            updatedItem.quantity = freegiftItemQuantity[idx];
            extraAddedFgs[idx] =
              item.quantity -
              freegiftItemQuantity[idx] -
              usedFg -
              milestoneReservedItemQuantity;
          } else if (
            !milestoneReservedItemQuantity &&
            !freegiftItemQuantity[idx] &&
            !totalFreeGiftUsedQuantity[idx]
          ) {
            updatedItem.quantity = 0;
            extraAddedFgs[idx] =
              item.quantity - usedFg - (reservedItemQuantity[idx] || 0);
          }
          if (freegiftItemQuantity[idx]) usedFgs[idx] = updatedItem.quantity;
        }
      });
    });
    return extraAddedFgs;
  };

  const extraAddedFgs = getExtraAddedFgs();

  const filteredCart = filterCartFgs(
    totalFreeGiftUsedQuantityAutoAdd,
    mrCart,
    reservedItemQuantity,
  );

  const config = {
    isEnabled: true,
    isRefresh: isRefreshPageAfterProductAddition,
    isAutoAdd: widget.triggerType !== 'checkout_page',
    isAutoRemove: isCartQuantityRestricted,
    allFreeGifts: combineGifts(allFreeGifts),
    freeGifts: combineGifts(freeGifts),
    mrCart: filteredCart.filter(item => item.quantity > 0),
    cartDiscountText,
    freeGiftAutoAddLimit,
    rulesEligibilityCount: 1,
    isConsiderReservedItemQuantity: true,
    widgetID,
    reservedItemQuantity: removeNotUsedFgsQuantityFromObject(
      filteredCart,
      reservedItemQuantity,
    ),
    isCalledFromCVB: true,
    shopifyFunctionsDiscountTitle: offer.shopifyFunctionsDiscountTitle,
  };

  await checkFgManualRewardsAndDisplay(
    fgMilestoneMap,
    allFgMilestoneMap,
    offer,
    req,
    sections,
    widget,
    cartPageDiscountConfig,
  );

  let cartChanged = false;
  cartChanged = await cvbFreeGiftAdd(config);

  const fgLimitsToReset = {};
  const updates = Object.keys(extraAddedFgs).reduce((acc, idx) => {
    const cartItem = req.cart[idx];
    if (extraAddedFgs[idx]) {
      acc[cartItem.key] = cartItem.quantity - extraAddedFgs[idx];
      fgLimitsToReset[cartItem.sp_variant_id] = 0;
    }
    return acc;
  }, {});
  if (isCartQuantityRestricted && Object.keys(updates).length) {
    const removalSuccess = await updateCart({ updates });
    if (removalSuccess) cartChanged = true;

    resetCVBFgVariantAutoAddLimit(fgLimitsToReset);
  }
  if (cartChanged && isRefreshPageAfterProductAddition) {
    goToCart({
      isPopup: false,
      isRefreshPageAfterProductAddition,
    });
  }

  return {
    cartAmount: newCartAmount,
    cartQuantity: newCartQuantity,
  };
}

export const resetCVBFgVariantAutoAddLimit = fgResetLimits => {
  const cvbFgExistingLimits = getCVBAutoAddedVariantsLimits();

  const updatedLimits = Object.keys(cvbFgExistingLimits).reduce((acc, key) => {
    Object.keys(fgResetLimits).forEach(fgVariantId => {
      if (acc[key] && acc[key][fgVariantId]) acc[key][fgVariantId] = 0;
    });
    return acc;
  }, cvbFgExistingLimits);
  setCVBAutoAddedVariantsLimits(updatedLimits);
};

const getFgVariantsMap = milestones => {
  const variantMap = {};

  milestones.forEach(milestone => {
    const { rewards } = milestone;
    rewards.forEach(reward => {
      if (reward.rewardType === 'freegift') {
        if (reward.freeGiftAvailingType === 'manual_selection') {
          reward.rewardProducts.forEach(product => {
            product.variants.forEach(variant => {
              variantMap[variant.id] = true;
            });
          });
        } else variantMap[reward.freeGiftSpVariantID] = true;
      }
    });
  });
  return variantMap;
};

const getFgManualOfferedProducts = milestones =>
  milestones
    .reduce((acc, milestone) => {
      const { rewards } = milestone;
      const manualRewards = rewards.filter(
        r =>
          r.rewardType === 'freegift' &&
          r.freeGiftAvailingType === 'manual_selection',
      );
      return [...acc, ...manualRewards];
    }, [])
    .reduce((acc, reward) => {
      const { rewardProducts } = reward;
      return [...acc, ...rewardProducts];
    }, []);

const getModifiedData = (offer, dataChanges) => ({ ...offer, ...dataChanges });

const getManualFgSections = sections =>
  sections.filter(s => s.sectionType.split('-')[0] === 'freegift').map(s => ({
    ...s,
    sectionType: s.sectionType.replace('freegift-', ''),
  }));

const combineGifts = gifts => {
  const giftsQuantityMap = {};
  const milestoneFgVariantMap = {};

  gifts.forEach(gift => {
    const { freeGiftSpVariantID, freeGiftQuantity, milestoneIdx } = gift;

    if (!milestoneFgVariantMap[freeGiftSpVariantID])
      milestoneFgVariantMap[freeGiftSpVariantID] = {};

    if (milestoneFgVariantMap[freeGiftSpVariantID][milestoneIdx])
      milestoneFgVariantMap[freeGiftSpVariantID][
        milestoneIdx
      ] += freeGiftQuantity;
    milestoneFgVariantMap[freeGiftSpVariantID][milestoneIdx] = freeGiftQuantity;

    giftsQuantityMap[freeGiftSpVariantID] =
      (giftsQuantityMap[freeGiftSpVariantID] || 0) + freeGiftQuantity;
  });

  return gifts
    .map(gift => {
      const updatedGift = JSON.parse(JSON.stringify(gift));
      updatedGift.freeGiftQuantity = giftsQuantityMap[gift.freeGiftSpVariantID];

      updatedGift.milestoneMap =
        milestoneFgVariantMap[gift.freeGiftSpVariantID];

      giftsQuantityMap[gift.freeGiftSpVariantID] = 0;
      return updatedGift;
    })
    .filter(gift => gift.freeGiftQuantity > 0);
};

const getTotalFreeGiftUsedQuantityAutoAdd = (
  milestones,
  useAllRewards = false,
) => {
  const freegiftUsed = {};

  milestones.forEach(milestone => {
    const isAutoAdd = milestone.rewards.some(
      reward => reward.freeGiftAvailingType === 'auto_add',
    );
    if (isAutoAdd || useAllRewards) {
      const { freegiftItemQuantity } = milestone;

      Object.keys(freegiftItemQuantity).forEach((idx, key) => {
        freegiftUsed[idx] =
          (freegiftUsed[idx] || 0) + freegiftItemQuantity[idx];
      });
    }
  });

  return freegiftUsed;
};

const filterCartFgs = (freegiftItemQuantity, cart, reservedItemQuantity) => {
  const filteredCart = cart.map((item, idx) => {
    const updatedItem = { ...item };
    const reservedItem = reservedItemQuantity[idx] || 0;

    if (
      freegiftItemQuantity[idx] &&
      item.quantity > freegiftItemQuantity[idx]
    ) {
      updatedItem.quantity = freegiftItemQuantity[idx] + reservedItem;
    } else if (!reservedItem && !freegiftItemQuantity[idx]) {
      updatedItem.quantity = 0;
    } else if (reservedItem && !freegiftItemQuantity[idx])
      updatedItem.quantity = reservedItem;
    return updatedItem;
  });

  return filteredCart;
};

const removeNotUsedFgsQuantityFromObject = (cart, object) => {
  const objectToUse = { ...object };
  const itemsArray = [];

  cart.forEach((item, idx) => {
    if (!item.quantity) objectToUse[idx] = 0;
    itemsArray[idx] = null;
  });

  Object.keys(objectToUse).forEach(key => (itemsArray[key] = objectToUse[key]));

  const updatedObject = {};
  itemsArray.filter(quantity => quantity !== 0).forEach((el, idx) => {
    if (el) updatedObject[idx] = el;
  });

  return updatedObject;
};

const checkFgManualRewardsAndDisplay = async (
  fgMilestoneMap,
  allFgMilestoneMap,
  offer,
  req,
  sections,
  widget,
  cartPageDiscountConfig,
) => {
  const {
    milestones,
    isCartRangeTriggerEnabled,
    primary,
    cartRange,
    theme,
    isCartQuantityRestricted,
    currencyCode,
    defaultCurrencyCartRange,
    isMultiCurrencyEnabled,
    isFreegiftDiscountCodeEnabled,
    freegiftDiscountCodes,
    isDiscountQuantityRestricted,
    isNumberOfOfferedProductsRestricted,
    offerLevelDiscountQuantity,
    discountQuantity,
    isEligibleOnlyForSubscription,
    reservedItemQuantity,
    rulesEligibilityCount,
    mr_offer_id,
  } = offer;
  const fgsBasedOnMilestone = getFgDataBasedOnMilestone(
    milestones,
    fgMilestoneMap,
    allFgMilestoneMap,
    'manual_selection',
    isCartQuantityRestricted,
  );

  logger.log('fgsBasedOnMilestone', fgsBasedOnMilestone);

  if (fgsBasedOnMilestone.length > 0) {
    const embeddedModule = await import('./embeddedModule');
    embeddedModule.setStore(store);
    fgsBasedOnMilestone.forEach(async (fg, idx) => {
      const modifiedTheme = getModifiedData(offer.theme, fg.theme);
      const filteredCart = filterCartFgs(
        fg.offer.freegiftItemQuantity,
        req.cart,
        fg.offer.reservedItemQuantity,
      );

      const offerToUse = {
        ...offer,
        theme: modifiedTheme,
      };

      fg.offer.reservedItemQuantity = removeNotUsedFgsQuantityFromObject(
        filteredCart,
        fg.offer.reservedItemQuantity,
      );
      fg.offer.freegiftItemQuantity = removeNotUsedFgsQuantityFromObject(
        filteredCart,
        fg.offer.freegiftItemQuantity,
      );
      const modifiedOffer = getModifiedData(offerToUse, fg.offer);

      await displayManualFg(
        modifiedOffer,
        getManualFgSections(sections),
        cartPageDiscountConfig,
        { ...widget, mrWidgetID: `${widget.mrWidgetID || 'fg'}-${idx}` },
        fg.isEligible,
        embeddedModule,
        {
          ...req,
          cart: filteredCart,
        },
      );
    });
  }
  const offered = getFgManualOfferedProducts(milestones);
  const config = {
    mr_offer_id,
    primary,
    isCartRangeTriggerEnabled,
    cartRange,
    cartDiscountText: theme.cartDiscountText,
    isAutoRemove: isCartQuantityRestricted,
    isAutoAdd: false,
    ttl: new Date().getTime() + freeGiftlocalStorageTTL,
    currencyCode,
    defaultCurrencyCartRange,
    isMultiCurrencyEnabled,
    isFreegiftDiscountCodeEnabled,
    freegiftDiscountCodes,
    isDiscountQuantityRestricted,
    isNumberOfOfferedProductsRestricted,
    offerLevelDiscountQuantity,
    discountQuantity,
    isEligibleOnlyForSubscription,
    isRefresh: theme.isRefreshPageAfterProductAddition,
    reservedItemQuantity,
    rulesEligibilityCount,
    offered,
  };
  if (offered.length) {
    const existingConfig = getCVBManualFgOfferConfig();
    existingConfig[widget.id] = config;

    setCVBManualFgOfferConfig(existingConfig);
  }
};

const displayManualFg = async (
  offer,
  sections,
  cartPageDiscountConfig,
  widget,
  is_eligible,
  embeddedModule,
  req,
) => {
  const { filterSections, displayEmbeddedWidget } = embeddedModule;
  const {
    onFreegiftNotEligibleResponse,
  } = await import('./commonModuleForEmbeddedAndAutoAdd');
  const {
    triggerType,
    mr_offer_id,
    allFreeGifts,
    primary: { sp_variant_id: primaryVariantId },
    isCartRangeTriggerEnabled,
    isAutoAdd,
    cartRange,
    currencyCode,
    defaultCurrencyCartRange,
    isMultiCurrencyEnabled,
    isFreegiftDiscountCodeEnabled,
    freegiftDiscountCodes,
    isEligibleOnlyForSubscription,
    discountQuantity,
    offerLevelDiscountQuantity,
    isDiscountQuantityRestricted,
    isNumberOfOfferedProductsRestricted,
    freeGiftAutoAddLimit,
    reservedItemQuantity,
    rulesEligibilityCount,
    widgetID,
    theme,
    isCartQuantityRestricted,
  } = offer;
  req.cart = req.cart.filter(item => item.quantity > 0);
  const offerType = 'freegift';
  const availingType = 'manual_selection';

  const { mrWidgetID } = widget;
  widget.offerType = offerType;
  widget.availingType = availingType;

  // functions to be called when offer is eligible
  let filteredSections = sections;
  if (is_eligible) {
    filteredSections = filterSections({
      sections,
      offerType,
      offer,
      triggerType,
    });

    await onManualFreeGiftEligibleResponse({
      offer,
      mrCart: req.cart,
      widget,
      isCalledFromCartDrawer: false,
      skipExecuteFgConditions: true,
    });

    // for free git auto add there is no widget to display
    if (offerType === 'freegift' && availingType === 'auto_add') {
      return;
    }
  }
  await displayEmbeddedWidget({
    offer,
    sections: filteredSections,
    cartPageDiscountConfig,
    isDiscountCodeEnabled: false,
    req,
    triggerType,
    offerType,
    availingType,
    mrWidgetID,
    is_eligible,
    widget,
    isCalledFromCartDrawer: false,
    widgetID: mrWidgetID,
  });

  if (!is_eligible) {
    await onFreegiftNotEligibleResponse({
      offerType,
      availingType,
      mrCart: req.cart,
      widget,
      useExistingConfig: true,
      existingConfig: {
        [widget.id]: {
          mr_offer_id,
          offered: allFreeGifts,
          primary: { sp_variant_id: primaryVariantId },
          isCartRangeTriggerEnabled,
          isAutoRemove: isCartQuantityRestricted,
          isAutoAdd,
          cartRange,
          currencyCode,
          defaultCurrencyCartRange,
          isMultiCurrencyEnabled,
          isFreegiftDiscountCodeEnabled,
          freegiftDiscountCodes,
          isRefresh: theme.isRefreshPageAfterProductAddition,
          cartDiscountText: theme.cartDiscountText,
          isEligibleOnlyForSubscription,
          discountQuantity,
          offerLevelDiscountQuantity,
          isDiscountQuantityRestricted,
          isNumberOfOfferedProductsRestricted,
          freeGiftAutoAddLimit,
          isAutoAddDisabled: true,
          reservedItemQuantity,
          rulesEligibilityCount,
          widgetID,
        },
      },
    });
  }
};

const getCVBContainerID = ({ sectionType, mrWidgetID }) => {
  // as the ids were predefined and old stores have this id, we cannot change it
  const trigger = 'cp';
  const offer = 'cvb';
  let section = sectionType;

  switch (sectionType) {
    case 'cart_page':
      section = 'page';
      break;

    case 'cart_drawer':
      section = 'drawer';
      break;

    default:
      break;
  }

  let id = `mr-div-embedded-${trigger}-${offer}-${section}`;
  if (mrWidgetID) {
    id = `${id}-${mrWidgetID}`;
  }
  return id;
};
